import { useState } from 'react';
import { motion } from 'framer-motion';
import { FaSave, FaEdit, FaUndo } from 'react-icons/fa';
import { useLocalStorage } from '../../hooks/useLocalStorage';

const ContentManager = () => {
  const [content, setContent] = useLocalStorage('tinytimes-content', defaultContent);
  const [activeSection, setActiveSection] = useState('hero');
  const [hasChanges, setHasChanges] = useState(false);

  const sections = [
    { id: 'hero', label: '🏠 Home Hero', icon: '🏠' },
    { id: 'about', label: 'ℹ️ About', icon: 'ℹ️' },
    { id: 'editions', label: '📚 Editions', icon: '📚' },
    { id: 'contact', label: '📞 Contact', icon: '📞' },
    { id: 'social', label: '🌐 Social Links', icon: '🌐' },
  ];

  const handleChange = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    // Content is auto-saved to localStorage
    setHasChanges(false);
    alert('Content saved successfully!');
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset to default content?')) {
      setContent(defaultContent);
      setHasChanges(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-display font-bold text-primary-green-dark">
          Content Manager
        </h2>
        <div className="flex gap-2">
          <motion.button
            onClick={handleReset}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold"
          >
            <FaUndo />
            <span>Reset</span>
          </motion.button>
          <motion.button
            onClick={handleSave}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={!hasChanges}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold ${
              hasChanges
                ? 'bg-primary-green text-white'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            <FaSave />
            <span>Save All</span>
          </motion.button>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex flex-wrap gap-2">
        {sections.map((section) => (
          <motion.button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-display font-semibold transition-all ${
              activeSection === section.id
                ? 'bg-primary-green text-white'
                : 'bg-white text-primary-green-dark hover:bg-primary-green/10'
            }`}
          >
            <span>{section.icon}</span>
            <span>{section.label}</span>
          </motion.button>
        ))}
      </div>

      {/* Content Editor */}
      <div className="card">
        {activeSection === 'hero' && (
          <HeroEditor content={content.hero} onChange={(field, value) => handleChange('hero', field, value)} />
        )}
        {activeSection === 'about' && (
          <AboutEditor content={content.about} onChange={(field, value) => handleChange('about', field, value)} />
        )}
        {activeSection === 'editions' && (
          <EditionsEditor content={content.editions} onChange={(field, value) => handleChange('editions', field, value)} />
        )}
        {activeSection === 'contact' && (
          <ContactEditor content={content.contact} onChange={(field, value) => handleChange('contact', field, value)} />
        )}
        {activeSection === 'social' && (
          <SocialEditor content={content.social} onChange={(field, value) => handleChange('social', field, value)} />
        )}
      </div>

      {/* Preview Notice */}
      <div className="card bg-accent-yellow/20 border-2 border-accent-yellow">
        <div className="flex items-center gap-3">
          <span className="text-4xl">💡</span>
          <div>
            <h3 className="font-display font-bold text-primary-green-dark">
              Content Auto-Saved
            </h3>
            <p className="text-neutral-gray-dark text-sm">
              Changes are automatically saved to your browser. For production, connect this to your CMS.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Hero Editor
const HeroEditor = ({ content, onChange }) => (
  <div className="space-y-4">
    <h3 className="text-xl font-display font-bold text-primary-green-dark mb-4">
      🏠 Home Page Hero Section
    </h3>

    <div>
      <label className="block font-semibold text-primary-green-dark mb-2">Title</label>
      <input
        type="text"
        value={content.title}
        onChange={(e) => onChange('title', e.target.value)}
        className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
      />
    </div>

    <div>
      <label className="block font-semibold text-primary-green-dark mb-2">Tagline</label>
      <input
        type="text"
        value={content.tagline}
        onChange={(e) => onChange('tagline', e.target.value)}
        className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
      />
    </div>

    <div>
      <label className="block font-semibold text-primary-green-dark mb-2">Subtitle</label>
      <textarea
        value={content.subtitle}
        onChange={(e) => onChange('subtitle', e.target.value)}
        rows={3}
        className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none resize-none"
      />
    </div>
  </div>
);

// About Editor
const AboutEditor = ({ content, onChange }) => (
  <div className="space-y-4">
    <h3 className="text-xl font-display font-bold text-primary-green-dark mb-4">
      ℹ️ About Page Content
    </h3>

    <div>
      <label className="block font-semibold text-primary-green-dark mb-2">Mission Statement</label>
      <textarea
        value={content.mission}
        onChange={(e) => onChange('mission', e.target.value)}
        rows={4}
        className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none resize-none"
      />
    </div>

    <div>
      <label className="block font-semibold text-primary-green-dark mb-2">Vision</label>
      <textarea
        value={content.vision}
        onChange={(e) => onChange('vision', e.target.value)}
        rows={3}
        className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none resize-none"
      />
    </div>

    <div>
      <label className="block font-semibold text-primary-green-dark mb-2">Story</label>
      <textarea
        value={content.story}
        onChange={(e) => onChange('story', e.target.value)}
        rows={5}
        className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none resize-none"
      />
    </div>
  </div>
);

// Editions Editor
const EditionsEditor = ({ content, onChange }) => (
  <div className="space-y-6">
    <h3 className="text-xl font-display font-bold text-primary-green-dark mb-4">
      📚 Editions Content
    </h3>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Micro Edition */}
      <div className="p-4 bg-accent-blue/10 rounded-xl">
        <h4 className="font-display font-bold text-lg text-primary-green-dark mb-3">
          🌈 Micro Edition
        </h4>
        <div className="space-y-3">
          <input
            type="text"
            value={content.micro.name}
            onChange={(e) => onChange('micro', { ...content.micro, name: e.target.value })}
            placeholder="Edition name"
            className="w-full px-3 py-2 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
          />
          <textarea
            value={content.micro.description}
            onChange={(e) => onChange('micro', { ...content.micro, description: e.target.value })}
            placeholder="Description"
            rows={3}
            className="w-full px-3 py-2 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none resize-none"
          />
        </div>
      </div>

      {/* Macro Edition */}
      <div className="p-4 bg-primary-green/10 rounded-xl">
        <h4 className="font-display font-bold text-lg text-primary-green-dark mb-3">
          🚀 Macro Edition
        </h4>
        <div className="space-y-3">
          <input
            type="text"
            value={content.macro.name}
            onChange={(e) => onChange('macro', { ...content.macro, name: e.target.value })}
            placeholder="Edition name"
            className="w-full px-3 py-2 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
          />
          <textarea
            value={content.macro.description}
            onChange={(e) => onChange('macro', { ...content.macro, description: e.target.value })}
            placeholder="Description"
            rows={3}
            className="w-full px-3 py-2 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none resize-none"
          />
        </div>
      </div>
    </div>
  </div>
);

// Contact Editor
const ContactEditor = ({ content, onChange }) => (
  <div className="space-y-4">
    <h3 className="text-xl font-display font-bold text-primary-green-dark mb-4">
      📞 Contact Information
    </h3>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block font-semibold text-primary-green-dark mb-2">Phone Number</label>
        <input
          type="text"
          value={content.phone}
          onChange={(e) => onChange('phone', e.target.value)}
          placeholder="+91 XXXXX XXXXX"
          className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
        />
      </div>

      <div>
        <label className="block font-semibold text-primary-green-dark mb-2">WhatsApp Number</label>
        <input
          type="text"
          value={content.whatsapp}
          onChange={(e) => onChange('whatsapp', e.target.value)}
          placeholder="+91 XXXXX XXXXX"
          className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
        />
      </div>

      <div className="md:col-span-2">
        <label className="block font-semibold text-primary-green-dark mb-2">Email Address</label>
        <input
          type="email"
          value={content.email}
          onChange={(e) => onChange('email', e.target.value)}
          placeholder="email@example.com"
          className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
        />
      </div>

      <div className="md:col-span-2">
        <label className="block font-semibold text-primary-green-dark mb-2">Google Form URL</label>
        <input
          type="url"
          value={content.googleFormUrl}
          onChange={(e) => onChange('googleFormUrl', e.target.value)}
          placeholder="https://forms.google.com/..."
          className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
        />
      </div>
    </div>
  </div>
);

// Social Links Editor
const SocialEditor = ({ content, onChange }) => (
  <div className="space-y-4">
    <h3 className="text-xl font-display font-bold text-primary-green-dark mb-4">
      🌐 Social Media Links
    </h3>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {Object.entries(content).map(([platform, url]) => (
        <div key={platform}>
          <label className="block font-semibold text-primary-green-dark mb-2 capitalize">
            {getSocialIcon(platform)} {platform}
          </label>
          <input
            type="url"
            value={url}
            onChange={(e) => onChange(platform, e.target.value)}
            placeholder={`https://${platform}.com/...`}
            className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
          />
        </div>
      ))}
    </div>
  </div>
);

const getSocialIcon = (platform) => {
  const icons = {
    facebook: '📘',
    instagram: '📸',
    twitter: '🐦',
    youtube: '📺',
    linkedin: '💼',
  };
  return icons[platform] || '🔗';
};

// Default Content
const defaultContent = {
  hero: {
    title: 'The Tiny Times',
    tagline: 'Weekly fun stories, puzzles & learning',
    subtitle: 'A delightful children\'s magazine that makes learning an adventure! Perfect for ages 4-15 🌟',
  },
  about: {
    mission: 'To inspire young minds through joyful learning experiences',
    vision: 'To be the most loved children\'s magazine in India',
    story: 'The Tiny Times was born from a simple idea: make learning irresistibly fun!',
  },
  editions: {
    micro: {
      name: 'Micro Edition',
      description: 'Perfect for young learners! Filled with colorful stories, simple puzzles, and fun activities.',
    },
    macro: {
      name: 'Macro Edition',
      description: 'Engaging content for older kids! News, challenging puzzles, creative writing, and more.',
    },
  },
  contact: {
    phone: '+91-9618377750',
    whatsapp: '+91-9618377750',
    email: 'thetinytimeschildrensnewspaper@gmail.com',
    googleFormUrl: 'https://forms.google.com/your-form-id',
  },
  social: {
    facebook: 'https://facebook.com/tinytimes',
    instagram: 'https://instagram.com/tinytimes',
    twitter: 'https://twitter.com/tinytimes',
    youtube: 'https://youtube.com/tinytimes',
  },
};

export default ContentManager;